/**
 * TRUS-709: a program's caseload model — Shared (everyone working on the
 * program sees all its members) or Individual (staff see only the members
 * assigned to them). It decides who sees member conversations when the org's
 * Member Inbox follows caseloads, and who sees members generally.
 *
 * The Edit program dialog must never write `caseload_model` unless the user
 * changed it:
 *  - the loaded value comes from the program list, else GET /api/programs/{id};
 *  - still unknown → "Unknown — choose", nothing selected, nothing sent;
 *  - on save, `caseload_model` goes out (PATCH, alone) only when the choice
 *    differs from the loaded value. The program PUT never carries it.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { programsAPI, type CaseloadModel } from '@/services/api/programs';

export function asCaseloadModel(v: unknown): CaseloadModel | null {
  return v === 'shared' || v === 'individual' ? v : null;
}

/** The PATCH body for the caseload model, or null when nothing changed / nothing was chosen. */
export function caseloadModelPatch(
  loaded: CaseloadModel | null,
  chosen: CaseloadModel | null,
): { caseload_model: CaseloadModel } | null {
  if (chosen == null || chosen === loaded) return null;
  return { caseload_model: chosen };
}

/**
 * Save the Edit program dialog's program fields, then — only if the user
 * changed it — the caseload model. The program body never carries
 * `caseload_model` (a stale or defaulted value must not overwrite the server's).
 */
export async function submitProgramUpdate<T>(opts: {
  programId: number;
  programData: Record<string, unknown>;
  loadedModel: CaseloadModel | null;
  chosenModel: CaseloadModel | null;
  updateProgram: (id: number, data: Record<string, unknown>) => Promise<T>;
  setCaseloadModel?: (id: number, model: CaseloadModel) => Promise<unknown>;
  /** A failed caseload PATCH is reported here; it never undoes the program save. */
  onCaseloadError?: (err: unknown) => void;
}): Promise<T> {
  const { caseload_model: _dropped, ...data } = opts.programData;
  void _dropped;
  const updated = await opts.updateProgram(opts.programId, data);
  const patch = caseloadModelPatch(opts.loadedModel, opts.chosenModel);
  if (updated && patch) {
    try {
      await (opts.setCaseloadModel ?? ((id, m) => programsAPI.setCaseloadModel(id, m)))(
        opts.programId,
        patch.caseload_model,
      );
    } catch (err) {
      opts.onCaseloadError?.(err);
    }
  }
  return updated;
}

/**
 * The program's loaded caseload model and the user's choice. `loaded` is
 * undefined while it is being fetched, null when the server does not say.
 */
export function useProgramCaseloadModel(program: { id: number; caseload_model?: unknown } | null) {
  const [loaded, setLoaded] = useState<CaseloadModel | null | undefined>(undefined);
  const [chosen, setChosen] = useState<CaseloadModel | null>(null);
  const programId = program?.id ?? null;
  const fromList = asCaseloadModel(program?.caseload_model);
  useEffect(() => {
    let cancelled = false;
    setChosen(null);
    if (programId == null) {
      setLoaded(undefined);
      return;
    }
    if (fromList) {
      setLoaded(fromList);
      return;
    }
    // The list did not carry it: the single-program read does.
    setLoaded(undefined);
    programsAPI
      .getProgram(programId)
      .then((p) => {
        if (!cancelled) setLoaded(asCaseloadModel((p as { caseload_model?: unknown })?.caseload_model));
      })
      .catch(() => {
        if (!cancelled) setLoaded(null);
      });
    return () => {
      cancelled = true;
    };
  }, [programId, fromList]);
  return {
    loaded,
    /** What the toggle shows: the user's choice, else the loaded value (null = unknown). */
    value: chosen ?? loaded ?? null,
    chosen,
    setChosen,
  };
}

interface ProgramCaseloadModelToggleProps {
  programId: number;
  /** The loaded value: undefined while loading, null when unknown. */
  loaded: CaseloadModel | null | undefined;
  /** What is selected (null = nothing). */
  value: CaseloadModel | null;
  onChange: (model: CaseloadModel) => void;
  disabled?: boolean;
}

export function ProgramCaseloadModelToggle({ programId, loaded, value, onChange, disabled }: ProgramCaseloadModelToggleProps) {
  const { t } = useAppTranslation();
  const loading = loaded === undefined;

  const option = (id: CaseloadModel, title: string, hint: string) => (
    <label
      className={cn(
        'flex flex-1 cursor-pointer items-start gap-2 rounded-lg border p-3',
        value === id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
      )}
    >
      <input
        type="radio"
        name={`caseload-model-${programId}`}
        value={id}
        checked={value === id}
        disabled={disabled || loading}
        onChange={() => onChange(id)}
        className="mt-1"
        data-testid={`caseload-model-${id}`}
      />
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );

  return (
    <fieldset className="space-y-2" data-testid="program-caseload-model">
      <legend className="flex items-center gap-2 text-base font-semibold">
        {t('admin.caseload_model', 'Caseload model')}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </legend>
      {!loading && value == null && (
        <p className="text-sm text-muted-foreground" data-testid="caseload-model-unknown">
          {t('admin.caseload_model_unknown', 'Unknown — choose')}
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        {option(
          'shared',
          t('admin.caseload_model_shared', 'Shared'),
          t('admin.caseload_model_shared_hint', 'Everyone on the program sees all its members.'),
        )}
        {option(
          'individual',
          t('admin.caseload_model_individual', 'Individual'),
          t('admin.caseload_model_individual_hint', 'Staff see only the members assigned to them.'),
        )}
      </div>
      {value != null && loaded !== undefined && value !== loaded && (
        <p className="text-xs text-muted-foreground" data-testid="caseload-model-pending">
          {t('admin.caseload_model_pending', 'Saved when you update the program.')}
        </p>
      )}
    </fieldset>
  );
}

export default ProgramCaseloadModelToggle;
